import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axios";
import { toast } from "sonner";
import BottomTabNav from "../components/BottomTabNav";
import ComingSoonModal from "../components/ComingSoonModal";
import { 
  Users, 
  Globe, 
  Star, 
  Clock, 
  Video, 
  MessageCircle,
  ArrowLeft,
  AlertCircle,
  Rocket,
  Check
} from "lucide-react";

// Language code to name mapping
const LANGUAGE_NAMES = {
  en: { en: "English", es: "Inglés", fr: "Anglais", ar: "الإنجليزية" },
  es: { en: "Spanish", es: "Español", fr: "Espagnol", ar: "الإسبانية" },
  fr: { en: "French", es: "Francés", fr: "Français", ar: "الفرنسية" },
  ar: { en: "Arabic", es: "Árabe", fr: "Arabe", ar: "العربية" }
};

// Language flags
const LANGUAGE_FLAGS = {
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  ar: "🇸🇦"
};

// Call method icons
const CALL_METHOD_ICONS = {
  zoom: Video,
  google_meet: Video,
  whatsapp: MessageCircle
};

const CALL_METHOD_LABELS = {
  zoom: "Zoom",
  google_meet: "Google Meet",
  whatsapp: "WhatsApp"
};

const AgentsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  
  const isRTL = i18n.language === 'ar';
  const primaryLanguage = localStorage.getItem('passportTalk_primaryLanguage') || 'en';
  const isEnglishUser = primaryLanguage === 'en';

  useEffect(() => {
    // If not English user, show restriction message
    if (!isEnglishUser) {
      setLoading(false);
      return;
    }
    
    fetchAgents();
  }, [isEnglishUser]);

  const fetchAgents = async () => {
    try {
      const response = await api.get('/live-agents');
      setAgents(response.data);
    } catch (err) {
      console.error("Failed to load agents:", err);
      setError(t('agents.loadError'));
      toast.error(t('agents.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const getLanguageName = (langCode) => {
    const names = LANGUAGE_NAMES[langCode];
    return names ? names[i18n.language] || names['en'] : langCode;
  };

  // Show Coming Soon page for Live Agents (launching June 2026)
  return (
    <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-travel-navy font-outfit mb-2" data-testid="agents-page-title">
            {t('agents.title')}
          </h1>
          <p className="text-slate-600">
            {t('agents.subtitle')}
          </p>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-2 border-emerald-200 p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Rocket className="w-10 h-10 text-white" />
          </div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
            <Clock className="w-4 h-4" />
            <span>Launching June 2026</span>
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 font-outfit mb-3">
            Live Agents Coming Soon
          </h2>
          
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Connect with real bilingual experts for live assistance with housing, banking, government paperwork, and more.
          </p>

          {/* Feature Preview */}
          <div className="bg-white rounded-2xl p-6 mb-6 text-left">
            <h3 className="font-semibold text-slate-800 mb-4">What to expect:</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-700">1-on-1 video/WhatsApp sessions</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-700">Bilingual experts in your language pair</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-700">Help with real-world situations</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-700">Affordable per-session pricing</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => setShowComingSoonModal(true)}
            className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
            data-testid="agents-notify-btn"
          >
            <Users className="w-5 h-5" />
            Be First to Access
          </button>
          
          <p className="text-xs text-slate-500 mt-4">
            VIP users get priority access when we launch
          </p>
        </div>

        {/* Use AI Translation in the meantime */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 text-center">
          <p className="text-slate-600 mb-4">
            In the meantime, use our AI-powered translator for instant help:
          </p>
          <button
            onClick={() => navigate('/conversation')}
            className="bg-aviation hover:bg-sky-600 text-white rounded-full px-8 py-3 font-semibold shadow-lg transition-all active:scale-95 flex items-center gap-2 mx-auto"
            data-testid="back-to-translator-btn"
          >
            <MessageCircle className="w-5 h-5" />
            Go to Translator
          </button>
        </div>
      </div>

      <BottomTabNav />

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName="Live Agents"
        launchDate="June 2026"
        source="agents_page"
      />
    </div>
  );
};

export default AgentsPage;
